import { useEffect, useMemo, useState, ReactNode } from "react";
import { AddChargebreakdownProvider } from "./context/AddChargebreakdownContext";
import { useElementHeight } from "@/hooks";
import useAddChargebreakdown from "./hooks/useAddChargebreakdown";
import { Button, Division, Heading, Flex, Icon, Icons, Modal, Paragraph, Chip, StepItemProps } from "@ir/ui";
import AddChargebreakdownMain from "./AddChargebreakdownMain";
import AddChargebreakdownSteps from "./AddChargebreakdownSteps";
import { IAddChargebreakdownHeaders } from "./interface/AddChargebreakdownInterface";

const AddChargebreakdownModalHeader = () => {
  const [modalHeaderRef, modalHeaderHeight] = useElementHeight<HTMLDivElement>(true);
  const { setModalHeaderHeight } = useAddChargebreakdown();

  const _modalHeaderHeight = useMemo(() => {
    return modalHeaderHeight + 35;
  }, [modalHeaderHeight]);

  useEffect(() => {
    setModalHeaderHeight(_modalHeaderHeight);
  }, [modalHeaderHeight, _modalHeaderHeight]);

  return (
    <Division ref={modalHeaderRef}>
      <Flex direction="column">
        <Heading className="font-bold display-sm text-primary-900">Add Charge Breakdown</Heading>
        <Paragraph className="font-normal text-grey-500 m-0">
          Drag and drop documents to automatically upload Charge Breakdown.
        </Paragraph>
      </Flex>
      <Flex direction="row">
        <AddChargebreakdownSteps />
      </Flex>
    </Division>
  );
};

const AddChargebreakdownModalFooter = ({ onCancel }: { onCancel: () => void }) => {
  const [modalFooterRef, modalFooterHeight] = useElementHeight<HTMLDivElement>(true);
  const {
    importedFileData,
    importedFileError,
    currentStep,
    continueToNextStep,
    continueToPreviousStep,
    setModalFooterHeight,
    getFinalChargebreakdownDataCount,
    getFinalChargebreakdownDataErrorsCount,
    getProcessedChargebreakdownSuccessCount,

    isSubmitting,
    isSubmitted,
  } = useAddChargebreakdown();

  const finalChargebreakdownDataErrorsCount = useMemo(() => {
    return getFinalChargebreakdownDataErrorsCount();
  }, [getFinalChargebreakdownDataErrorsCount]);

  const finalChargebreakdownDataCount = useMemo(() => {
    return getFinalChargebreakdownDataCount();
  }, [getFinalChargebreakdownDataCount]);

  const processedChargebreakdownSuccessCount = useMemo(() => {
    return getProcessedChargebreakdownSuccessCount();
  }, [getProcessedChargebreakdownSuccessCount]);

  const isContinueDisabled = useMemo(() => {
    if (currentStep === "upload-chargebreakdown") {
      return !(importedFileData !== null && importedFileError === null);
    }
    if (currentStep === "verify-chargebreakdown") {
      return (
        finalChargebreakdownDataErrorsCount > 0 || finalChargebreakdownDataCount === 0 || isSubmitting || isSubmitted
      );
    }
    return false;
  }, [
    importedFileData,
    importedFileError,
    currentStep,
    finalChargebreakdownDataErrorsCount,
    finalChargebreakdownDataCount,
    isSubmitting,
    isSubmitted,
  ]);

  const onContinue = () => {
    continueToNextStep();
  };

  const onBack = () => {
    continueToPreviousStep();
  };

  useEffect(() => {
    setModalFooterHeight(modalFooterHeight + 35);
  }, [modalFooterHeight]);

  // Success and error logic for verify-chargebreakdown step
  // if there is an error on any of the chargebreakdown data, then submit button should be disabled
  const showCloseButton =
    isSubmitted && currentStep === "verify-chargebreakdown" && finalChargebreakdownDataErrorsCount === 0;

  return (
    <Flex ref={modalFooterRef} justifyContent="space-between" direction="row" className="w-100">
      <Flex align="center" direction="row" gap={1}>
        {finalChargebreakdownDataErrorsCount > 0 && currentStep === "verify-chargebreakdown" && (
          <Chip
            color="error"
            shape="square"
            variant="filled"
            label={`${finalChargebreakdownDataErrorsCount} Charge breakdown(s) have error(s)`}
            size="large"
          />
        )}
        {processedChargebreakdownSuccessCount > 0 && isSubmitted && currentStep === "verify-chargebreakdown" && (
          <Chip
            color="success"
            shape="square"
            variant="filled"
            label={`${processedChargebreakdownSuccessCount} Chargebreakdown(s) processed`}
            size="large"
          />
        )}
      </Flex>
      <Division>
        <Button color="grey" variant="clear" onPress={onCancel}>
          Close
        </Button>
        {currentStep !== "upload-chargebreakdown" && !showCloseButton && (
          <Button color="primary" variant="outline" onPress={onBack} isDisabled={isSubmitting || isSubmitted}>
            Back
          </Button>
        )}
        {/* Default submit/continue button */}
        {!showCloseButton && (
          <Button color="primary" onPress={onContinue} isDisabled={isContinueDisabled} style={{ width: 85 }}>
            {currentStep === "verify-chargebreakdown" ? "Submit" : "Continue"}
          </Button>
        )}
      </Division>
    </Flex>
  );
};

interface AddChargebreakdownModalProps {
  deductionId: number;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  triggerButton?: ReactNode;
  triggerButtonText?: string;
  triggerButtonIcon?: Icons;
  customHeaders?: IAddChargebreakdownHeaders[];
  customSteps?: StepItemProps[];
  shouldShowChargeBreakdown?: boolean;
}

const AddChargebreakdownModal = ({
  deductionId,
  onSuccess,
  onError,
  onClose,
  triggerButton,
  triggerButtonText = "Add Charge",
  triggerButtonIcon = Icons.Plus,
  customHeaders,
  customSteps,
  shouldShowChargeBreakdown = true,
}: AddChargebreakdownModalProps) => {
  const [isAddChargebreakdownModalOpen, setIsAddChargebreakdownModalOpen] = useState(false);

  const handleChargebreakdownModalClose = () => {
    setIsAddChargebreakdownModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleSuccess = (response: any) => {
    if (onSuccess) {
      onSuccess(response);
    }
    setIsAddChargebreakdownModalOpen(false);
  };

  const handleError = (error: any) => {
    if (onError) {
      onError(error);
    }
  };

  return (
    <AddChargebreakdownProvider
      deductionId={deductionId}
      onSuccess={handleSuccess}
      onError={handleError}
      onClose={handleChargebreakdownModalClose}
      customHeaders={customHeaders}
      customSteps={customSteps}
    >
      <Modal
        isOpen={isAddChargebreakdownModalOpen}
        onClose={handleChargebreakdownModalClose}
        isFullScreen={true}
        scrollable
        zIndex={10}
        headerContent={<AddChargebreakdownModalHeader />}
        footerContent={<AddChargebreakdownModalFooter onCancel={handleChargebreakdownModalClose} />}
        wrapClassName="upload-chargebreakdown-modal"
      >
        <AddChargebreakdownMain />
      </Modal>

      {shouldShowChargeBreakdown &&
        (triggerButton || (
          <Button size="medium" color="primary" className="ms-1" onPress={() => setIsAddChargebreakdownModalOpen(true)}>
            <Icon name={triggerButtonIcon} size="medium" slot="start" />
            {triggerButtonText}
          </Button>
        ))}
    </AddChargebreakdownProvider>
  );
};

export default AddChargebreakdownModal;
